import { Redirect, router, useFocusEffect } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import * as React from 'react';
import { useCallback, useState } from 'react';
import { KeyboardAvoidingView, Platform } from 'react-native';
import {
    BodyLargeEmphasized,
    CheckIcon,
    Icon,
    isWeb,
    SafeAreaView,
    ScrollView,
    Separator,
    tokens,
    useToastController,
    View,
    YStack,
} from 'smoothie';

import { isEmpty } from '../../../common/misc';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { transformBillFromServer } from '../../../common/utils/billUtils';
import type { AccountSummary } from '../../../holistic/types/HolisticSummary';
import LogRocketFacade from '../../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../../logger/Logger';
import useApproveBill from '../../hooks/useApproveBill';
import useEditProposedChanges from '../../hooks/useEditProposedChanges';
import useRemoveBill from '../../hooks/useRemoveBill';
import useUpdateBill, { type UpdateBillBody } from '../../hooks/useUpdateBill';
import {
    activeBillsAtom,
    allBillsAtom,
    billsAccountsAtom,
    currentlyEditingBillFieldAtom,
    isEditingBillTransactionsAtom,
    selectedBillAtom,
} from '../../state/BillsAtoms';
import {
    type _Bill,
    AiToPostgresBillingCadence,
    BillActivationStatus,
    BillContext,
    BillCreationType,
    EditProposedChangesRequestAction,
    type IBill,
    type InlineEditableBillFields,
} from '../../types/Bill';
import { BillIcon, BillIconSize } from '../common/BillIcons';
import BillsHeader from '../common/BillsHeader';
import BillDetailsAttributes from './BillDetailsAttributes';
import BillDetailsExcludeButton from './BillDetailsExcludeButton';
import BillDetailsNewBillButtons from './BillDetailsNewBillButtons';
import BillDetailsProposedChanges from './BillDetailsProposedChanges';
import BillDetailsProposedChangesSheet from './BillDetailsProposedChangesSheet';
import BillDetailsRemoveSheet from './BillDetailsRemoveSheet';
import BillDetailsTransactions from './BillDetailsTransactions';

const BillDetails = () => {
    LogRocketFacade.tagPage('BillDetails');
    const toast = useToastController();
    const user = useAuthedUser();
    const [selectedBill, setSelectedBill] = useAtom(selectedBillAtom);
    const billsAccounts = useAtomValue(billsAccountsAtom);
    const storeActiveBills = useSetAtom(activeBillsAtom);
    const storeAllBills = useSetAtom(allBillsAtom);
    const setCurrentlyEditingBillField = useSetAtom(
        currentlyEditingBillFieldAtom,
    );
    const setIsEditingBillTransactions = useSetAtom(
        isEditingBillTransactionsAtom,
    );
    const [isRemoveSheetOpen, setIsRemoveSheetOpen] = useState(false);
    const [isProposedChangesSheetOpen, setIsProposedChangesSheetOpen] =
        useState(false);
    const [proposedChangesAction, setProposedChangesAction] =
        useState<EditProposedChangesRequestAction | null>(null);

    const { mutate: updateBill } = useUpdateBill();

    const { mutate: approveBill } = useApproveBill();
    const { mutate: removeBill } = useRemoveBill();
    const { mutate: editProposedChanges } = useEditProposedChanges();

    // leave bill/transaction editing state when closing modal
    useFocusEffect(
        useCallback(() => {
            return () => {
                setCurrentlyEditingBillField(null);
                setIsEditingBillTransactions(false);
            };
        }, []),
    );

    if (!selectedBill) {
        captureException(
            new Error('Attempted to load BillDetails with no selected Bill'),
        );
        return <Redirect href="/bills/bills-overview" />;
    }

    const storeUpdatedBill = (bill: Partial<_Bill>): IBill => {
        const transformedBill = transformBillFromServer(bill);

        storeAllBills((prev) => {
            return prev.map((pb) => {
                return pb.id === transformedBill.id ? transformedBill : pb;
            });
        });

        if (bill.activationStatus === BillActivationStatus.ACTIVE) {
            storeActiveBills((prev) => {
                return prev.map((pb) => {
                    return pb.id === transformedBill.id ? transformedBill : pb;
                });
            });
        }

        return transformedBill;
    };

    const submitBillApproval = () => {
        approveBill(
            {
                urlParams: {
                    id: selectedBill.id ?? '',
                    membershipId: user?.membership?.id ?? '',
                },
                body: null,
            },
            {
                onSuccess: (updatedBillFromServer) => {
                    toast.show('Approved', {
                        description: 'Total amount updated',
                    });
                    storeUpdatedBill(updatedBillFromServer);
                    router.back();
                },
                onError: (error) => {
                    toast.show('Something went wrong');
                    captureException(error);
                    // TODO handle error UX
                },
            },
        );
    };

    const submitBillRemoval = () => {
        removeBill(
            {
                urlParams: {
                    id: selectedBill.id ?? '',
                    membershipId: user?.membership?.id ?? '',
                },
                body: null,
            },
            {
                onSuccess: (updatedBillFromServer) => {
                    toast.show('Removed', {
                        description: 'Total amount updated',
                    });
                    storeUpdatedBill(updatedBillFromServer);
                    router.back();
                },
                onError: (error) => {
                    toast.show('Something went wrong');
                    captureException(error);
                    // TODO handle error UX
                },
            },
        );
    };

    const submitProposedChanges = (
        action: EditProposedChangesRequestAction,
    ) => {
        const { accountId, averageAmount, frequency } =
            selectedBill?.proposedChanges ?? {};

        editProposedChanges(
            {
                urlParams: {
                    billId: selectedBill?.id ?? '',
                    membershipId: user?.membership?.id ?? '',
                },
                body: {
                    action,
                    ...(accountId != null && { accountId }),
                    ...(averageAmount != null && {
                        amount: Math.round(averageAmount * 100),
                    }),
                    ...(frequency != null && {
                        cadence: AiToPostgresBillingCadence[frequency],
                    }),
                },
            },
            {
                onSuccess: (updatedBillFromServer) => {
                    const transformedBill = storeUpdatedBill(
                        updatedBillFromServer,
                    );
                    setSelectedBill(transformedBill);
                    toast.show(
                        action === EditProposedChangesRequestAction.APPROVE
                            ? 'Changes applied'
                            : 'No changes made',
                        {
                            description:
                                action ===
                                EditProposedChangesRequestAction.APPROVE
                                    ? 'This bill has been updated'
                                    : 'This bill has not been updated',
                            icon: (
                                <View
                                    alignItems="center"
                                    justifyContent="center"
                                    backgroundColor="$green1000"
                                    borderRadius={12}
                                    padding={9}
                                >
                                    <Icon
                                        icon={CheckIcon}
                                        size={24}
                                        color="$white"
                                    />
                                </View>
                            ),
                        },
                    );
                },
                onError: (error) => {
                    toast.show('Something went wrong');
                    captureException(error);
                    // TODO handle error UX
                },
                onSettled: () => {
                    setIsProposedChangesSheetOpen(false);
                    setProposedChangesAction(null);
                },
            },
        );
    };

    const submitBillUpdate = (
        name: keyof UpdateBillBody,
        value: string | number,
    ) => {
        // optimistically update UI with new bill, and fallback if the update fails
        const oldBill = selectedBill;
        const newBill = { ...selectedBill, [name]: value };
        setSelectedBill(newBill);

        const {
            id,
            accountId,
            displayName,
            payee,
            amount,
            cadence,
            timingLabel,
            timingDescription,
            category,
            lastPaidAt,
            memberMarkedAsSwitched,
        } = selectedBill as IBill;

        updateBill(
            {
                urlParams: {
                    id,
                    membershipId: user?.membership?.id ?? '',
                },
                body: {
                    accountId,
                    displayName,
                    payee,
                    amount,
                    cadence,
                    timingLabel,
                    timingDescription,
                    category,
                    lastPaidAt,
                    memberMarkedAsSwitched,
                    [name]: value,
                } satisfies UpdateBillBody,
            },
            {
                onSuccess: (updatedBillFromServer) => {
                    storeUpdatedBill(updatedBillFromServer);
                    const { displayName } = updatedBillFromServer;

                    toast.show(`${displayName} bill updated`, {
                        icon: (
                            <View
                                alignItems="center"
                                justifyContent="center"
                                backgroundColor="$green1000"
                                borderRadius={12}
                                padding={9}
                            >
                                <Icon
                                    icon={CheckIcon}
                                    size={24}
                                    color="$white"
                                />
                            </View>
                        ),
                    });
                },
                onError: (error) => {
                    setSelectedBill(oldBill);
                    toast.show('Something went wrong');
                    captureException(error);
                    // TODO handle error UX
                },
            },
        );
    };

    const {
        icon,
        color,
        accountId,
        transactions,
        proposedChanges,
        activationStatus,
        creationType,
    } = selectedBill;

    const account = billsAccounts?.find(
        (a) => a.id === accountId,
    ) as AccountSummary;

    return (
        <>
            <StatusBar style="light" />
            <SafeAreaView edges={['right', 'left']}>
                <KeyboardAvoidingView
                    style={{
                        flex: 1,
                        height: '100%',
                        ...(isWeb && {
                            justifyContent: 'center',
                            alignItems: 'center',
                        }),
                    }}
                    behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
                    keyboardVerticalOffset={90}
                >
                    <YStack
                        width="100%"
                        flex={1}
                        padding={20}
                        maxWidth={700}
                        alignSelf="center"
                        paddingBottom={0}
                        justifyContent="space-between"
                        gap={16}
                    >
                        <BillsHeader
                            onBack={() => {
                                setSelectedBill(null);
                                setCurrentlyEditingBillField(null);
                                router.back();
                            }}
                            title="Details"
                        />
                        <ScrollView
                            showsVerticalScrollIndicator={isWeb}
                            contentContainerStyle={{ paddingBottom: 20 }}
                        >
                            {activationStatus ===
                                BillActivationStatus.REVIEW && (
                                <BodyLargeEmphasized>
                                    We found a new bill for you to review. If
                                    anything looks off, you can make edits
                                    below.
                                </BodyLargeEmphasized>
                            )}
                            <YStack
                                backgroundColor={tokens.color.light.brand.white}
                                height="100%"
                                separator={<Separator />}
                            >
                                {proposedChanges != null && (
                                    <BillDetailsProposedChanges
                                        onActionPress={(
                                            action: EditProposedChangesRequestAction,
                                        ) => {
                                            setProposedChangesAction(action);
                                            setIsProposedChangesSheetOpen(true);
                                        }}
                                    />
                                )}

                                <YStack gap={16} paddingTop={20}>
                                    <BillIcon
                                        color={color}
                                        icon={icon}
                                        size={BillIconSize.MEDIUM}
                                        hasShadow={true}
                                        borderRadius={20}
                                    />
                                    <BillDetailsAttributes
                                        bill={selectedBill}
                                        account={account}
                                        onInlineAttributePress={(
                                            fieldName: InlineEditableBillFields,
                                        ) => {
                                            isEmpty(proposedChanges) &&
                                                setCurrentlyEditingBillField(
                                                    fieldName,
                                                );
                                        }}
                                        onEdit={submitBillUpdate}
                                    />
                                </YStack>
                                {creationType === BillCreationType.AUTO && (
                                    <BillDetailsTransactions
                                        transactions={transactions ?? []}
                                        account={account}
                                        context={BillContext.DEFAULT}
                                    />
                                )}
                                {activationStatus ===
                                    BillActivationStatus.ACTIVE && (
                                    <BillDetailsExcludeButton
                                        onPress={() => {
                                            setIsRemoveSheetOpen(true);
                                        }}
                                    />
                                )}
                            </YStack>
                        </ScrollView>
                        <View paddingBottom={isWeb ? 20 : undefined}>
                            {activationStatus ===
                                BillActivationStatus.REVIEW && (
                                <BillDetailsNewBillButtons
                                    onApprove={submitBillApproval}
                                    onReject={submitBillRemoval}
                                />
                            )}
                            {isRemoveSheetOpen && (
                                <BillDetailsRemoveSheet
                                    header="Not a bill"
                                    title="Are you sure this isn’t a bill?"
                                    subText="Removing this bill will update your total amount moving forward."
                                    editMode={false}
                                    onDismiss={() => {
                                        setIsRemoveSheetOpen(false);
                                    }}
                                    onExclude={submitBillRemoval}
                                />
                            )}
                            {isProposedChangesSheetOpen &&
                                proposedChangesAction != null && (
                                    <BillDetailsProposedChangesSheet
                                        bill={selectedBill}
                                        action={proposedChangesAction}
                                        onSubmit={submitProposedChanges}
                                        onDismiss={() => {
                                            setIsProposedChangesSheetOpen(
                                                false,
                                            );
                                            setProposedChangesAction(null);
                                        }}
                                    />
                                )}
                        </View>
                    </YStack>
                </KeyboardAvoidingView>
            </SafeAreaView>
        </>
    );
};

export default BillDetails;
